import {
  CalendarOutlined,
  EditOutlined,
  HistoryOutlined,
  LeftOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  DatePicker,
  Empty,
  message,
  Popover,
  Space,
  Spin,
  Timeline,
} from 'antd';
import moment from 'moment';
import React, { useState } from 'react';

import { fetchAPI } from '../../utils/api';
import { formatUTCTimeToLocal } from '../../utils/format';
import { safelyParseJSON } from '../Ticketing/SubmittedTicketList';
import { OrderPaymentStatus } from './utils';

const OrderPaymentDate = (props: {
  value: string;
  orderId: number;
  status: OrderPaymentStatus;
}) => {
  const [value, setValue] = useState({
    real: props.value ? moment(props.value) : null,
    temp: props.value ? moment(props.value) : null,
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const savePaymentDate = async () => {
    setLoading(true);
    try {
      await fetchAPI(`order/specific-update/${props.orderId}`, {
        method: 'PATCH',
        payload: {
          payment_date: value.temp.format('YYYY-MM-DD') + ' 10:00:00',
        },
      });
      message.success('Success update payment date');
      setValue(val => ({ ...val, real: val.temp }));
      setEditing(false);
    } catch (error) {
      message.error('Failed update payment date');
      setValue(val => ({ ...val, temp: val.real }));
    }
    setLoading(false);
  };

  if (editing) {
    return (
      <DatePicker
        format={'DD MMM YYYY'}
        value={value.temp}
        placeholder="Select date"
        onChange={newDate => {
          if (!newDate) {
            setEditing(false);
          } else {
            setValue(val => ({ ...val, temp: newDate }));
          }
        }}
        onBlur={() => savePaymentDate()}
        disabled={loading}
      />
    );
  }

  if (!value.real) {
    return null;
  }

  return (
    <>
      <span className="m-label">Payment Date</span>
      {formatUTCTimeToLocal(
        value.real.format('YYYY-MM-DD HH:mm:ss'),
        'DD MMM YYYY'
      )}{' '}
      {props.status === OrderPaymentStatus.PAID && (
        <EditOutlined
          onClick={() => {
            setEditing(true);
            setValue(val => ({ ...val, temp: val.real }));
          }}
        />
      )}{' '}
      <OrderPaymentDateHistory orderId={props.orderId} />
    </>
  );
};

const OrderPaymentDateHistory = (props: { orderId: number }) => {
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const histories = useQuery(
    ['order-payment-date-history', visible, page, pageSize, props.orderId],
    async () => {
      if (!visible) {
        return {
          items: [],
          page,
          per_page: pageSize,
          total: 0,
          loading: true,
        };
      }

      try {
        const resp = await fetchAPI('user/activity', {
          payload: {
            table_name: 'order',
            value_type: 'update_specific_data',
            row_id: props.orderId,
          },
        });

        return { ...resp, loading: false };
      } catch (error) {
        message.error('Failed get history');
        return {
          items: [],
          page,
          per_page: pageSize,
          total: 0,
          loading: false,
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        items: [],
        page,
        per_page: pageSize,
        total: 0,
        loading: true,
      },
    }
  );
  return (
    <Popover
      title="History Payment Date"
      trigger={'click'}
      visible={visible}
      onVisibleChange={vis => setVisible(vis)}
      placement="rightTop"
      content={
        <Spin spinning={histories.data.loading || histories.isLoading}>
          {!histories.data.items.length ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Timeline style={{ maxWidth: 230 }}>
              {histories.data.items.map(item => {
                const oldDate = safelyParseJSON(item.old_value)?.payment_date;
                const newDate = safelyParseJSON(item.new_value)?.payment_date;
                return (
                  <Timeline.Item dot={<CalendarOutlined />} style={{}}>
                    Change Date from{' '}
                    <b>
                      {oldDate
                        ? moment(oldDate).format('DD MMM YYYY')
                        : 'nothing'}
                    </b>{' '}
                    to <b>{moment(newDate).format('DD MMM YYYY')}</b>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '11px',
                        opacity: '0.7',
                      }}
                    >
                      {formatUTCTimeToLocal(
                        item?.created_at,
                        'DD MMM YYYY HH:mm'
                      )}
                      <div style={{ fontSize: '11px' }}>
                        <UserOutlined /> By {item?.user?.full_name}
                      </div>
                    </span>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          )}

          {(page > 1 || histories.data.total > page * pageSize) && (
            <Space>
              <Button
                icon={<LeftOutlined />}
                onClick={() => setPage(p => p - 1)}
                disabled={page <= 1}
              />
              <Button
                icon={<RightOutlined />}
                onClick={() => setPage(p => p + 1)}
                disabled={histories.data.total <= page * pageSize}
              />
            </Space>
          )}
        </Spin>
      }
    >
      <HistoryOutlined />
    </Popover>
  );
};
export default OrderPaymentDate;
